import { Link, navigate, useLocation } from "../navigation";
import { useDeferredValue, useState } from "react";
import { BookOpen, Check, RotateCcw } from "lucide-react";
import type { LibrarySummary } from "../../../../packages/published-content/library";
import { AssignDialog } from "../AssignDialog";
import { PageHeading, RequestState } from "../PortalShell";
import { useResource } from "../portal-data";
import { TestRow } from "./test-list";
import { SearchTests } from "./test-list";
import { testCatalogStatus } from "./test-catalog-status";
import { defaultExamArchiveFilters, examArchiveFilterKeys, filterAndSortExams, normalizeExamArchiveFilters, type ExamArchiveFilters } from "../../../../packages/exam-catalog/browser";
import { publishedExamArchiveItem } from "./exam-browser";

export function AdminTests() {
  const library = useResource<{ release: LibrarySummary }>("/api/library");
  const location = useLocation();
  const [assign, setAssign] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const exams = library.data?.release.exams ?? [];
  const fullCount = exams.filter((exam) => testCatalogStatus(exam) === "full").length;
  const partialCount = exams.length - fullCount;
  const items = exams.map(publishedExamArchiveItem);
  const filters = normalizeExamArchiveFilters(Object.fromEntries(examArchiveFilterKeys.map((key) => [key, location.searchParams.get(key) ?? (key === "query" ? location.searchParams.get("search") : undefined)])), items);
  const settledQuery = useDeferredValue(filters.query);
  const filtered = filterAndSortExams(items, { ...filters, query: settledQuery });
  const years = [...new Set(items.map((item) => item.year))].sort().reverse();
  const regions = [...new Set(items.map((item) => item.regionCategory))].sort();
  const hasFilters = examArchiveFilterKeys.some((key) => filters[key] !== defaultExamArchiveFilters[key]);
  const updateFilters = (next: ExamArchiveFilters) => {
    const target = new URL(window.location.href);
    target.searchParams.delete("search");
    for (const key of examArchiveFilterKeys) {
      if (next[key] === defaultExamArchiveFilters[key]) target.searchParams.delete(key);
      else target.searchParams.set(key, next[key]);
    }
    navigate(target.pathname + target.search, true);
  };
  const setFilter = (key: keyof ExamArchiveFilters, value: string) => updateFilters({ ...filters, [key]: value });

  return (
    <>
      <PageHeading eyebrow="Test library" title="Tests">
        <div className="heading-stat">
          <strong>{exams.length}</strong>
          <span>{fullCount} full · {partialCount} partial</span>
        </div>
      </PageHeading>
      <div className="portal-content">
        <div className="page-toolbar admin-exam-controls" aria-label="Exam filters">
          <div className="catalog-search"><span>Search exams</span><SearchTests search={filters.query} onSearch={(value) => setFilter("query", value)} placeholder="Title, exam ID, region, form, or date" /></div>
          <CatalogSelect label="Sort" value={filters.sort} onChange={(value) => setFilter("sort", value)} options={[
            ["quality", "Best quality and fullest"], ["newest", "Newest sitting"], ["oldest", "Oldest sitting"], ["coverage", "Highest coverage"], ["questions", "Most represented questions"], ["title", "Title A-Z"], ["easiest", "Easiest first"], ["hardest", "Hardest first"]
          ]} />
          <CatalogSelect label="Readiness" value={filters.readiness} onChange={(value) => setFilter("readiness", value)} options={[["all", "All readiness states"], ["ready", "Ready to use"], ["playable", "Partial but playable"], ["browse", "Browse only"]]} />
          <CatalogSelect label="Form completeness" value={filters.coverage} onChange={(value) => setFilter("coverage", value)} options={[["all", "Full and partial forms"], ["full_sat", "Full SAT exams (98/98)"], ["partial", "Not full SAT exams"], ["complete", "Any complete form or section"]]} />
          <CatalogSelect label="Identity" value={filters.identity} onChange={(value) => setFilter("identity", value)} options={[["all", "All identity states"], ["verified", "Verified"], ["probable", "Probable"], ["unresolved", "Unresolved"], ["conflicting", "Conflicting"]]} />
          <CatalogSelect label="Year" value={filters.year} onChange={(value) => setFilter("year", value)} options={[["all", "All years"], ...years.map((year): [string, string] => [year, year])]} />
          <CatalogSelect label="Region" value={filters.region} onChange={(value) => setFilter("region", value)} options={[["all", "All regions"], ...regions.map((region): [string, string] => [region, region])]} />
          <button className="secondary-button" type="button" disabled={!hasFilters} onClick={() => updateFilters(defaultExamArchiveFilters)}><RotateCcw size={14} /> Reset</button>
          <span className="catalog-result-count" aria-live="polite">{filtered.length} of {exams.length} {exams.length === 1 ? "form" : "forms"}</span>
        </div>
        {(filters.sort === "easiest" || filters.sort === "hardest") && <p className="test-card-detail">Sorted by median editorial difficulty band (1–7) for the available fixed route. Partial ratings show their coverage; unrated forms appear last.</p>}
        <RequestState loading={library.loading} error={library.error} retry={library.reload} />
        {message && (
          <p className="success-message" role="status">
            <Check size={16} />
            {message} <Link href="/admin/assignments">View assignments</Link>
          </p>
        )}
        <div className="test-list">
          {filtered.map(({ exam }) => (
            <TestRow key={exam.id} exam={exam} admin onAction={() => setAssign(exam.id)} />
          ))}
        </div>
        {library.data && !filtered.length && (
          <div className="portal-empty">
            <BookOpen size={24} />
            <h2>No tests found</h2>
            <p>No published tests match these filters.</p>
            <button className="secondary-button" type="button" onClick={() => updateFilters(defaultExamArchiveFilters)}>Clear search and filters</button>
          </div>
        )}
      </div>
      {assign && (
        <AssignDialog
          exams={exams}
          initialExamId={assign}
          onClose={() => setAssign(null)}
          onAssigned={() => { setAssign(null); setMessage("Test assigned."); }}
        />
      )}
    </>
  );
}

function CatalogSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (value: string) => void; options: Array<[string, string]> }) {
  return <label className="catalog-filter"><span>{label}</span><select value={value} onChange={(event) => onChange(event.target.value)}>{options.map(([id, text]) => <option key={id} value={id}>{text}</option>)}</select></label>;
}
